/**
 * Invite Member Modal
 *
 * Modal dialog for inviting new team members with email and role selection.
 *
 * @module components/team/InviteMemberModal
 */

import { useState, type FormEvent } from 'react';
import { Modal, Input, Button } from '@/components/ui';
import type { OrgRole } from '@/lib/firebase-types';

// =============================================================================
// Types
// =============================================================================

interface InviteMemberModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Called when the modal should close */
  onClose: () => void;
  /** Called when invitation is submitted */
  onInvite: (email: string, role: OrgRole) => Promise<void>;
  /** Roles the current user can invite as */
  invitableRoles: OrgRole[];
}

/** Role descriptions for the UI */
const ROLE_DESCRIPTIONS: Record<OrgRole, string> = {
  owner: 'Full access. Can transfer ownership and delete the organization.',
  org_admin: 'Full access except transferring ownership or deleting the organization. Can receive ownership.',
  billing_admin: 'Admin powers plus billing management.',
  admin: 'Can manage API keys, view usage, and invite/remove admin, manager, and member roles.',
  manager: 'Can manage API keys, view usage, and invite/remove members only.',
  member: 'View-only access. Can view API keys and usage statistics.',
};

/** Role display names */
const ROLE_LABELS: Record<OrgRole, string> = {
  owner: 'Owner',
  org_admin: 'Org Admin',
  billing_admin: 'Billing Admin',
  admin: 'Admin',
  manager: 'Manager',
  member: 'Member',
};

// =============================================================================
// Component
// =============================================================================

export function InviteMemberModal({
  open,
  onClose,
  onInvite,
  invitableRoles,
}: InviteMemberModalProps) {
  const [email, setEmail] = useState('');
  // Default to member if available, otherwise first invitable role
  const [role, setRole] = useState<OrgRole>(
    invitableRoles.includes('member') ? 'member' : invitableRoles[0] || 'member'
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError('Email is required.');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    try {
      await onInvite(email.trim().toLowerCase(), role);
      // Reset form and close
      setEmail('');
      setRole('member');
      onClose();
    } catch (err) {
      // Extract error message from Firebase HttpsError
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to send invitation.';
      // Clean up Firebase error messages
      const cleanMessage = errorMessage
        .replace(/^Firebase:/, '')
        .replace(/\(auth\/.*\)\.?$/, '')
        .trim();
      setError(cleanMessage || 'Failed to send invitation.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setEmail('');
      setRole('member');
      setError(null);
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Invite Team Member"
      description="Send an invitation to join your organization."
      closeOnBackdropClick={!loading}
      closeOnEscape={!loading}
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={loading}>
            Send Invitation
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Email Input */}
        <Input
          label="Email Address"
          type="email"
          placeholder="teammate@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={error || undefined}
          disabled={loading}
          autoFocus
        />

        {/* Role Selection */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Role
          </label>
          <div className="space-y-2">
            {invitableRoles.map((r) => (
              <label
                key={r}
                className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
              >
                <input
                  type="radio"
                  name="role"
                  value={r}
                  checked={role === r}
                  onChange={() => setRole(r)}
                  disabled={loading}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium text-gray-900">{ROLE_LABELS[r]}</div>
                  <div className="text-sm text-gray-500">
                    {ROLE_DESCRIPTIONS[r]}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>
      </form>
    </Modal>
  );
}
